import { flatten } from 'ramda';
import React from 'react';

import { connect } from 'react-redux';
import { Form } from 'reactstrap';
import { bindActionCreators } from 'redux';

import { SUPER_USER } from '../../actionTypes/Users/UserActionType';
import Api from '../../Api';
import { ModalContainer } from '../Common';
import SelectInput from '../Common/Inputs/SelectInput';
import FlashMessages from '../Common/Messages/FlashMessages';
import './Feedback.scss';

const api = new Api();
const AUTO_HIDE_ON_SUBMIT = 5 * 1000;
const MAX_CHAR_FEEDBACK_TEXT = 300;

const getDefaultState = () => ({
  number: '',
  msg: '',
  comment: '',
  length: MAX_CHAR_FEEDBACK_TEXT,
  selected: false,
  disabled: false,
  feedback: false,
  validation: true,
  option: '',
  reportId: null,
  autoHideTimerId: null,
  formSubmitted: false,
});

class Feedback extends React.Component {
  state = getDefaultState();

  validateAndSubmitFeedback = async ({ setOpened }) => {
    this.setState({
      formSubmitted: true,
    });

    if (this.state.number === '' || (this.state.option === 'Yes' && !this.state.reportId)) {
      this.errorCall('Waiting for your feedback');
      return;
    }
    const myInit = {
      body: {
        npsScore: this.state.number,
        comments: this.state.comment,
        ...(this.props.sessionUserRole === SUPER_USER
          ? {}
          : {
              reportId: this.state.reportId,
            }),
      },
    };
    this.setState({
      disabled: true,
    });

    try {
      await api.postData('/rest/productfeedback', myInit);
      const autoHideTimerId = setTimeout(() => {
        setOpened(false);
      }, AUTO_HIDE_ON_SUBMIT);
      this.setState({
        feedback: true,
        autoHideTimerId,
      });
      this.errorCall()
    } catch (err) {
      console.error(err);
      this.setState({ disabled: false });
      this.errorCall(err && err.response && err.response.data.error);
    }
  };

  errorCall = (msg) => {
    this.setState({ msg });
  };

  handleInputChange = (inputFields, value) => {
    this.setState({
      [inputFields.name]: value,
    });
  };

  handleRowClick = (item) => {
    this.setState({
      selected: true,
      number: item,
    });
  };

  handleOptionClick = (option) => {
    this.setState({
      option: option,
      reportId: null,
    });
  };

  onChange = (evt) => {
    const totalLength = MAX_CHAR_FEEDBACK_TEXT - evt.target.value.length;
    if (totalLength >= 0) {
      this.setState({
        [evt.target.name]: evt.target.value,
        length: totalLength,
      });
    }
  };

  renderBullets() {
    const pointerList = new Array(11).fill(0).map((_, index) => (
      <li
        tabIndex="0"
        role="radio"
        aria-checked={this.state.selected && this.state.number === index}
        aria-labelledby={`feedback_radio_recommended_score_${index}_lbl`}
        id={`feedback_radio_recommended_score_${index}`}
        key={`feedback_radio_recommended_score_${index}`}
        onClick={() => this.handleRowClick(index)}
        onKeyDown={({ key }) => (key === 'Enter' ? this.handleRowClick(index) : void 0)}
        className={this.state.selected && this.state.number === index ? 'active' : 'inactive'}>
        <p>
          <i
            className={[
              'far',
              this.state.selected && this.state.number === index ? 'fa-dot-circle selected' : ' fa-circle',
            ].join(' ')}></i>
          <i className="far fa-dot-circle"></i>
        </p>
        <p id={`feedback_radio_recommended_score_${index}_lbl`} className="numbers">
          {index}
        </p>
      </li>
    ));
    return pointerList;
  }

  renderOptions() {
    const optionsList = ['Yes', 'No'].map((item) => (
      <li
        role="radio"
        tabIndex="0"
        aria-checked={this.state.option === item}
        aria-labelledby={`feedback_radio_variant_score_${item.toLowerCase()}_lbl`}
        id={`feedback_radio_variant_score_${item.toLowerCase()}`}
        key={`feedback_radio_variant_score_${item.toLowerCase()}`}
        onClick={() => this.handleOptionClick(item)}
        onKeyDown={({ key }) => (key === 'Enter' ? this.handleOptionClick(item) : void 0)}
        className={'options-list ' + (this.state.option === item ? 'active' : 'inactive')}>
        <span>
          <i className={'far ' + (this.state.option === item ? 'fa-dot-circle selected' : ' fa-circle')}></i>
          <i className="far fa-dot-circle"></i>
        </span>
        <span id={`feedback_radio_variant_score_${item.toLowerCase()}_lbl`} className="numbers pLeft-5">
          {item}
        </span>
      </li>
    ));
    return optionsList;
  }

  hideFlashMessage = () => {
    this.setState({ msgShow: false });
  };

  getToggleButton = (opened, toggleFn) => {
    return (
      <button
        type="button"
        aria-label="submit feedback"
        className="feedback v-middle-align"
        title="submit feedback"
        onClick={() => toggleFn(true)}>
        <span className="feedback-action">Submit Feedback</span>
        <span className="pen-container">
          <i className="far fa-pen"></i>
        </span>
      </button>
    );
  };

  getModalBody = () => {
    const { msg } = this.state;
    const reportList = [
      {
        id: null,
        name: 'Choose...',
      },
      {
        id: this.props.dashboard.reportId,
        name: this.props.dashboard.reportDisplayName,
      },
      ...flatten(this.props.reportMenu.map((item) => item.reports)).map((item) => ({
        id: item.reportId,
        name: item.reportDisplayName,
      })),
    ];
    return (
      <React.Fragment>
        <div className="message-placeholder" aria-live="polite">
          <FlashMessages
            close={this.hideFlashMessage}
            classNames="fl-feedback"
            type='error'
            msg={msg}
            open={!!msg}
            scope="SendFeedbackModal"
            ariaDescription="Send Feedback Error:"
          />
        </div>
        {!this.state.feedback ? (
          <Form className="modal-form">
            {!this.props.sessionUserRole || this.props.sessionUserRole === SUPER_USER ? (
              void 0
            ) : (
              <React.Fragment>
                <div className="mBottom-20">
                  <span
                    className="block mBottom-10 text-left"
                    id="heading_group_report"
                    role="term"
                    aria-label="Is this feedback related to a specific report? (Required)">
                    <span className="lsn-red">*</span>
                    Is this feedback related to a specific report?
                  </span>
                  <ul
                    role="radiogroup"
                    aria-labelledby="heading_group_report"
                    aria-activedescendant="feedback_radio_variant_score_yes"
                    className="ul-cust bullets-list text-left">
                    {this.renderOptions()}
                  </ul>
                </div>
                {this.state.option === 'Yes' ? (
                  <div className="mBottom-20">
                    <SelectInput
                      onInputChange={this.handleInputChange}
                      errorMessages={this.state.formSubmitted && !this.state.reportId}
                      required="required"
                      title="Report"
                      name="reportId"
                      labelSize={12}
                      dataObj={reportList}
                      value={this.state.reportId}
                    />
                  </div>
                ) : (
                  void 0
                )}
              </React.Fragment>
            )}
            <div className="mBottom-20">
              <span
                className="block mBottom-10 text-left"
                id="scoreTitle"
                role="term"
                aria-label="On a scale of zero to ten, how likely are you to recommend Analytics by Liaison to others? (Required)">
                <span className="lsn-red">*</span>
                On a scale of zero to ten, how likely are you to recommend Analytics by Liaison to others?
              </span>
              <ul
                role="radiogroup"
                aria-labelledby="scoreTitle"
                aria-activedescendant="feedback_radio_recommended_score_0"
                className="ul-cust bullets-list bullets-list-score">
                {this.renderBullets()}
              </ul>
            </div>
            <div className="mBottom-0">
              <span id="sbFormTitle" className="block mBottom-10 text-left">
                Additional Comments: ({this.state.length} characters left)
              </span>
              <textarea
                aria-labelledby="sbFormTitle"
                name="comment"
                value={this.state.comment}
                className="type-here"
                onChange={(evt) => this.onChange(evt)}></textarea>
            </div>
          </Form>
        ) : (
          <div className="right-tick feedback-success verticle-middle">
            <i className="far fa-check-circle"></i>
            <span role="alert" className="pLeft-15 lsn-check">
              Feedback Sent
            </span>
          </div>
        )}
      </React.Fragment>
    );
  };

  render() {
    return (
      <ModalContainer
        aria-label="Send Feedback"
        asToggleButtonFn={this.getToggleButton}
        showCloseIcon
        preventEscape
        title="Send Feedback"
        className="feedback"
        text={this.getModalBody()}
        onClosed={() => {
          clearTimeout(this.state.autoHideTimerId);
          this.setState(getDefaultState());
        }}
        buttons={
          this.state.feedback
            ? false
            : [
                {
                  onClick: this.validateAndSubmitFeedback,
                  label: 'Add',
                  disabled: this.state.disabled,
                },
              ]
        }
      />
    );
  }
}

function mapStateToProps(state) {
  return {
    reportMenu: state.Page.reportMenus.reportMenus,
    dashboard: state.Page.reportMenus.dashboard,
    sessionUserRole: state.AppSession.userDetails.userRole,
  };
}
function matchDispatchToProps(dispatch) {
  return bindActionCreators({}, dispatch);
}

export default connect(mapStateToProps, matchDispatchToProps)(Feedback);
